import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { products } from '@/mocks/products';

export default function FeaturedProducts() {
  const { ref, isVisible } = useScrollAnimation();
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className="py-24 px-4 md:px-8 bg-dark"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`mb-14 flex flex-col sm:flex-row sm:items-end justify-between gap-6 reveal ${isVisible ? 'visible' : ''}`}>
          <div>
            <p className="text-accent text-xs font-semibold tracking-[3px] uppercase mb-3">
              Hamva Range
            </p>
            <h2 className="font-poppins font-bold text-3xl md:text-5xl text-white">
              Featured Solutions
            </h2>
            <div className="h-1 w-16 bg-accent rounded-full mt-4" />
          </div>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-sm text-accent font-semibold hover:gap-3 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            View All Products <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product, i) => (
            <div
              key={product.id}
              className={`product-card relative rounded-2xl overflow-hidden flex flex-col reveal reveal-delay-${(i % 3) + 1} ${isVisible ? 'visible' : ''}`}
              style={{ background: '#1A2130' }}
              onMouseEnter={() => setHovered(product.id)}
              onMouseLeave={() => setHovered(null)}
            >
              {/* Badge */}
              {product.badge && (
                <div className="absolute top-4 left-4 z-10 bg-accent px-3 py-1 rounded-full text-xs font-semibold text-white whitespace-nowrap">
                  {product.badge}
                </div>
              )}

              {/* Product Image */}
              <Link
                to={`/product/${product.id}`}
                className="relative aspect-[4/3] overflow-hidden block cursor-pointer flex-shrink-0"
                style={{ background: '#FFFFFF' }}
              >
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-center transition-transform duration-500"
                  style={{ transform: hovered === product.id ? 'scale(1.06)' : 'scale(1)' }}
                />
              </Link>

              {/* Card Content */}
              <div className="p-5 flex flex-col flex-1">
                <p className="text-accent text-xs font-medium tracking-wider uppercase mb-1.5">
                  {product.category}
                </p>
                <Link
                  to={`/product/${product.id}`}
                  className="block font-poppins font-bold text-base text-white mb-2 leading-snug hover:text-accent transition-colors duration-200 cursor-pointer"
                >
                  {product.name}
                </Link>
                <p className="text-text-muted text-sm leading-relaxed mb-3 line-clamp-2 flex-1">
                  {product.benefit}
                </p>

                {/* Size tags */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {product.sizes.map((size) => (
                    <span
                      key={size}
                      className="px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap"
                      style={{ background: 'rgba(255,255,255,0.06)', color: '#8A8FA8' }}
                    >
                      {size}
                    </span>
                  ))}
                </div>

                {/* Two Buttons */}
                <div className="flex gap-2 mt-auto">
                  <Link
                    to={`/product/${product.id}`}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold text-white cursor-pointer whitespace-nowrap transition-all duration-200 border border-white/15 hover:border-accent/50 hover:text-accent"
                    style={{ background: 'rgba(255,255,255,0.05)' }}
                  >
                    <i className="ri-eye-line text-sm" />
                    View Details
                  </Link>
                  <a
                    href={`https://wa.me/923007321011?text=Hi%20Hamva%2C%20I%20am%20interested%20in%20${encodeURIComponent(product.name)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex-1 wa-btn flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold text-white cursor-pointer whitespace-nowrap transition-all duration-300 ${hovered === product.id ? 'red-glow' : ''}`}
                  >
                    <i className="ri-whatsapp-line text-sm" />
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
